import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";

import { fetchProfile } from "../utils/fetchProfile";
import Navbar from "../Components/Navbar";

import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";

const PROFILE_API_URL = `${process.env.REACT_APP_API_URL}/api/v1/auth/profile`;

function Profile() {
  const [dataProfile, setDataProfile] = useState({});
  const accessToken = Cookies.get("access_token") || "";
  const [notAllowAccessPage, setNotAllowAccessPage] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [formData, setFormData] = useState({
    full_name: "",
    password: "",
  });

  useEffect(() => {
    fetchProfile(
      accessToken,
      (data) => {
        setDataProfile(data);
        setLoading(false);
      },
      setNotAllowAccessPage
    );
  }, [accessToken]);

  useEffect(() => {
    if (dataProfile) {
      setFormData({
        full_name: dataProfile.full_name || "",
        password: "",
      });
    }
  }, [dataProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setError(null);
    setSuccessMessage("");

    if (!formData.full_name) {
      setError("Please fill in your full name.");
      return;
    }

    setLoading(true);

    const payload = {
      full_name: formData.full_name,
    };
    if (formData.password) {
      payload.password = formData.password;
    }

    try {
      const response = await fetch(PROFILE_API_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage("Profile updated successfully!");
        setDataProfile((prev) => ({
          ...prev,
          full_name: payload.full_name,
        }));
        setFormData((prev) => ({ ...prev, password: "" }));
      } else {
        setError(data.message || "Failed to update profile.");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  if (notAllowAccessPage) {
    return (
      <div>
        <Navbar
          active="Profile"
          initial={dataProfile.initial}
          role_code={dataProfile.role_code}
        />
        <Container sx={{ mt: 4 }}>
          <Alert severity="error">
            You are not allowed to access this page.
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(to right, #9ef5ffff, #3eb1beff)",
      }}
    >
      <Navbar
        active="Profile"
        initial={dataProfile.initial}
        role_code={dataProfile.role_code}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Container maxWidth="sm" disableGutters>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 4 },
              borderRadius: 4,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {loading && !dataProfile.full_name ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}
                >
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
                      fontSize: "2rem",
                    }}
                  >
                    {dataProfile?.initial || dataProfile?.full_name?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" component="h1" fontWeight="bold">
                      {dataProfile.full_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      User Profile
                    </Typography>
                  </Box>
                </Box>

                <Box component="form" noValidate>
                  <Typography variant="h6" gutterBottom>
                    Edit Profile
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                    />
                    <TextField
                      fullWidth
                      label="New Password (optional)"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      helperText="Leave blank to keep your current password."
                    />

                    {error && <Alert severity="error">{error}</Alert>}
                    {successMessage && (
                      <Alert severity="success">{successMessage}</Alert>
                    )}

                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={2}
                      sx={{ pt: 2 }}
                    >
                      <Button
                        sx={{
                          backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
                          border: "1px solid hsl(186, 89.90%, 27.30%)",
                          borderRadius: "4px",
                          color: "#fff",
                          fontSize: { xs: "14px", sm: "16px" },
                          padding: "4px 10px",
                          textTransform: "none",
                          "&:hover": {
                            backgroundImage:
                              "linear-gradient(#0991a2, #057d8c)",
                          },
                        }}
                        onClick={handleSave}
                        disabled={loading}
                        startIcon={
                          loading ? <CircularProgress size={20} /> : null
                        }
                      >
                        {loading ? "Updating..." : "Update Profile"}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </>
            )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}

export default Profile;
